import React, { useState } from "react";

import type { MenuProps, TableProps } from "antd";
import { Checkbox, Col, Dropdown, Row, Table, Tag, Typography } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { formatDateForJustMonthDayAndYear } from "../../../helpers/date";
import { getUserType } from "../../../helpers/userDetails";
import TailSpinLoader from "../Loaders/TailSpinLoader";
import NetwalaPagination from "../Pagination/NetwalaPagination";
import CATVIcon from "./../../../assets/svgs/CATVIcon.svg";
import INTERNETIcon from "./../../../assets/svgs/INTERNETIcon.svg";
import IPTVIcon from "./../../../assets/svgs/IPTVIcon.svg";
import IPWhiteListIcon from "./../../../assets/svgs/IPWhiteList.svg";
import PromisedTransactionIcon from "./../../../assets/svgs/PromisedTransactionIcon.svg";
import BulkUpdateAreaModal from "../../Modals/BulkUpdateAreaModal";
import "./index.css";

const NetwalaUsersTable: React.FC<UsersTableProp> = ({
  users,
  pagination,
  handlePageSwitch,
  handlePageSizeChange,
  isLoading,
}) => {
  const navigate = useNavigate();
  const [checkboxMode, setCheckboxMode] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showUpdateAreaModal, setShowUpdateAreaModal] =
    useState<boolean>(false);

  const handleCheckboxModeToggle = () => {
    setCheckboxMode(!checkboxMode);
    if (checkboxMode) {
      setSelectedRowKeys([]);
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleMenuClick = (e: any) => {
    switch (e.key) {
      case "UPDATE_AREA":
        setShowUpdateAreaModal(true);
        break;
    }
  };

  const handleCloseUpdateAreaModal = (reload?: boolean) => {
    setShowUpdateAreaModal(false);
    if (reload) {
      setSelectedRowKeys([]);
      // Reload the current page to refresh the data after bulk area update
      handlePageSwitch(pagination.currentPage);
    }
  };

  const selectedConnectionIds = selectedRowKeys.map((key) => Number(key));

  const actionMenuItems: MenuProps["items"] = [
    {
      key: "UPDATE_AREA",
      label: (
        <Typography className="profile-drop-down-item">Update Area</Typography>
      ),
    },
  ];

  const columns: TableProps<CompanyConnection>["columns"] = [
    {
      title: "Full Name",
      // dataIndex: "intPkHeadID",
      // key: "intPkHeadID",
      render: (row) => (
        <span style={{ display: "flex" }}>
          {row.totalOpenPromises > 0 ? (
            <img
              src={PromisedTransactionIcon}
              style={{ maxWidth: "20px", marginLeft: "-10px" }}
              className="cursor-pointer"
            />
          ) : null}{" "}
          <span>{row.fullName}</span>
        </span>
      ),
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 150,
      render: (text) => (
        <span
          style={{
            wordBreak: "break-word",
            whiteSpace: "normal",
            maxWidth: "150px",
            display: "inline-block",
          }}
        >
          {text || "-"}
        </span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (text) => <Tag style={{ fontWeight: "bold" }}>{text} </Tag>,
    },
    {
      title: "Area",
      dataIndex: "areaName",
      key: "areaName",
    },
    {
      title: "ISP",
      dataIndex: "ispName",
      key: "ispName",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
    },
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      render: (text) => (
        <span>
          {text ? formatDateForJustMonthDayAndYear(new Date(text)) : "---"}
        </span>
      ),
    },
    {
      title: "Pending Invoices",
      dataIndex: "totalPendingInvoices",
      key: "totalPendingInvoices",
      render: (text) => (
        <Tag
          color={text > 0 ? "warning" : "success"}
          style={{ fontWeight: "bold" }}
        >
          {text > 0 ? text : "---"}{" "}
        </Tag>
      ),
    },
    {
      title: "Type",
      align: "right",
      dataIndex: "",
      render: (row) => {
        return (
          <div className="users-table-action-container">
            {/* <img src={TicketsIcon} className="cursor-pointer" /> */}
            {row.connectionType === "CATV" ? (
              <img src={CATVIcon} className="cursor-pointer" />
            ) : null}
            {row.connectionType === "INTERNET" ? (
              <img src={INTERNETIcon} className="cursor-pointer" />
            ) : null}
            {row.totalIPTVServices > 0 ? (
              <img src={IPTVIcon} className="cursor-pointer" />
            ) : null}
            {row.totalStaticIpServices > 0 ? (
              <img src={IPWhiteListIcon} className="cursor-pointer" />
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="netwala-users-table-container">
      {showUpdateAreaModal && (
        <BulkUpdateAreaModal
          show={showUpdateAreaModal}
          handleClose={handleCloseUpdateAreaModal}
          connectionIds={selectedConnectionIds}
        />
      )}
      {isLoading === true ? (
        <TailSpinLoader />
      ) : (
        <Row>
          <Col span={24}>
            {getUserType() === "CEO" && (
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                {selectedRowKeys.length > 0 && (
                  <Dropdown
                    menu={{
                      items: actionMenuItems,
                      onClick: handleMenuClick,
                    }}
                    placement="bottomRight"
                  >
                    <MoreOutlined
                      style={{
                        fontSize: "20px",
                        cursor: "pointer",
                        color: "#352890",
                   
                      }}
                    />
                  </Dropdown>
                )}
                <Checkbox
                  checked={checkboxMode}
                  onChange={handleCheckboxModeToggle}
                >
                  Select
                </Checkbox>
              </div>
            )}
            <Table
              className="netwala-users-table striped-table"
              columns={columns}
              dataSource={users}
              rowKey={(record) => record.intPkConnectionID}
              scroll={{ x: "max-content" }}
              rowSelection={
                getUserType() === "CEO" && checkboxMode
                  ? rowSelection
                  : undefined
              }
              onRow={(record, rowIndex) => {
                return {
                  onClick: (event) => {
                    if (!checkboxMode) {
                      navigate(
                        `/user-connection-details/${record.intPkHeadID}/${record.intPkConnectionID}?activeTab=1&fullName=${record.fullName}`
                      );
                    }
                  }, // click row
                };
              }}
              // pagination={{
              //   pageSize: 10,
              //   showQuickJumper: true,
              //   showSizeChanger: true,
              //   pageSizeOptions: ["10", "20", "30", "40"],
              // }}
              pagination={false}
            />
          </Col>
          <Col
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
              marginBottom: "20px",
            }}
            span={24}
          >
            <NetwalaPagination
              pagination={pagination}
              handlePageSwitch={handlePageSwitch}
              handlePageSizeChange={handlePageSizeChange}
            />
          </Col>
        </Row>
      )}
    </div>
  );
};
export default NetwalaUsersTable;
